import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, ExternalLink, Globe2, Search } from 'lucide-react';

const ACTION_LABELS = {
  search: 'Search',
  open_page: 'Opened page',
  find_in_page: 'Found in page',
};

const getDomainName = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch (error) {
    return url;
  }
};

const normalizeCalls = (calls = []) => (
  Array.isArray(calls) ? calls : []
).filter((call) => call && typeof call === 'object');

const getActionQueries = (action = {}) => {
  if (Array.isArray(action.queries) && action.queries.length > 0) {
    return action.queries.filter(Boolean);
  }
  return action.query ? [action.query] : [];
};

const getStatusLabel = (status) => {
  if (status === 'searching' || status === 'in_progress') return 'Searching';
  if (status === 'completed') return 'Completed';
  if (status === 'failed') return 'Failed';
  return 'Web activity';
};

const WebSearchTrace = ({ calls, status }) => {
  const [expanded, setExpanded] = useState(false);
  const normalizedCalls = useMemo(() => normalizeCalls(calls), [calls]);

  if (normalizedCalls.length === 0) return null;

  const latestStatus = status || normalizedCalls[normalizedCalls.length - 1]?.status;
  const allSources = normalizedCalls
    .flatMap((call) => call.action?.sources || [])
    .filter((source) => source?.url);
  const uniqueSources = Array.from(
    new Map(allSources.map((source) => [source.url, source])).values()
  );

  return (
    <div className="my-3 max-w-full rounded-lg border border-gray-200 bg-gray-50/80 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-300">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left"
        onClick={() => setExpanded((value) => !value)}
      >
        <span className="flex min-w-0 items-center gap-2">
          <Search size={14} className="shrink-0" />
          <span className="font-medium">Web search</span>
          <span className="truncate text-gray-500 dark:text-gray-400">
            {getStatusLabel(latestStatus)}
          </span>
        </span>
        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {expanded && (
        <div className="border-t border-gray-200 px-3 py-2 dark:border-gray-700">
          <div className="space-y-3">
            {normalizedCalls.map((call, index) => {
              const action = call.action || {};
              const queries = getActionQueries(action);
              const actionLabel = ACTION_LABELS[action.type] || action.type || 'Web action';
              const sources = Array.isArray(action.sources) ? action.sources.filter((source) => source?.url) : [];

              return (
                <div key={call.id || index} className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-medium">{actionLabel}</span>
                    <span className="text-gray-500 dark:text-gray-400">
                      {getStatusLabel(call.status)}
                    </span>
                  </div>

                  {queries.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {queries.map((query) => (
                        <span
                          key={query}
                          className="max-w-full rounded-md bg-white px-2 py-1 font-mono text-[11px] text-gray-800 shadow-sm dark:bg-gray-800 dark:text-gray-100"
                        >
                          {query}
                        </span>
                      ))}
                    </div>
                  )}

                  {action.url && (
                    <a
                      href={action.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex max-w-full items-center gap-1 text-gray-700 underline decoration-gray-300 underline-offset-2 hover:text-black dark:text-gray-300 dark:decoration-gray-600 dark:hover:text-white"
                    >
                      <Globe2 size={12} className="shrink-0" />
                      <span className="truncate">{action.title || getDomainName(action.url)}</span>
                      <ExternalLink size={11} className="shrink-0" />
                    </a>
                  )}

                  {action.pattern && (
                    <div className="font-mono text-[11px] text-gray-600 dark:text-gray-400">
                      {action.pattern}
                    </div>
                  )}

                  {sources.length > 0 && (
                    <div className="space-y-1">
                      <div className="text-gray-500 dark:text-gray-400">Sources consulted</div>
                      <div className="flex flex-wrap gap-1.5">
                        {sources.slice(0, 8).map((source, sourceIndex) => (
                          <a
                            key={`${source.url}-${sourceIndex}`}
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex max-w-full items-center gap-1 rounded-md bg-white px-2 py-1 text-[11px] text-gray-700 hover:text-black dark:bg-gray-800 dark:text-gray-300 dark:hover:text-white"
                          >
                            <span className="truncate">{source.title || getDomainName(source.url)}</span>
                            <ExternalLink size={10} className="shrink-0" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {queries.length === 0 && !action.url && !action.pattern && sources.length === 0 && (
                    <div className="text-gray-500 dark:text-gray-400">
                      No query details were returned for this call.
                    </div>
                  )}
                </div>
              );
            })}

            {uniqueSources.length > 0 && (
              <div className="border-t border-gray-200 pt-2 dark:border-gray-700">
                <div className="text-gray-500 dark:text-gray-400">
                  {uniqueSources.length} total source{uniqueSources.length === 1 ? '' : 's'} returned
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WebSearchTrace;
